import { useEffect, useState } from "react";
import { X, Search, UserPlus, AlertCircle, Check, Copy } from "lucide-react";
import { AxiosError } from "axios";
import { api } from "@/lib/api";

interface UsuarioResumen {
  id: string;
  keycloakSubject: string | null;
  correo: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string | null;
  tipo: string;
  estatus: string;
}

interface UsuarioCreadoResponse extends UsuarioResumen {
  rol: string;
  passwordInicial: string;
}

const ROLES_OPCIONES = [
  { value: "TRAMITADOR", label: "Tramitador" },
  { value: "TITULAR", label: "Titular" },
  { value: "USUARIO_GESTOR", label: "Gestor" },
  { value: "REPRESENTANTE_LEGAL", label: "Representante legal" },
];

type Estado = "buscar" | "encontrado" | "crear" | "creado-exito";

/**
 * Flujo "asociar usuario existente o crear nuevo":
 *
 * 1. Estado "buscar": input correo + botón Buscar.
 *    - Si existe → estado "encontrado" con card del usuario y botón Asociar.
 *    - Si no existe (404) → estado "crear" con form de alta.
 * 2. Estado "crear": form nombre/apellidos/rol → POST /api/iam/usuarios.
 *    - Al éxito: estado "creado-exito" con la contraseña inicial visible (copy).
 * 3. Al confirmar asociación (o al cerrar el exito-modal del alta), llama
 *    onAsociar(usuarioId) con el ID a vincular al recurso del caller.
 */
export function UserSearchOrCreate({
  open,
  title,
  onClose,
  onAsociar,
  correoInicial,
  hint,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  /** notificar=true → enviar correo de asociación al RL. El caller decide qué endpoint. */
  onAsociar: (usuarioId: string, notificar: boolean) => Promise<void>;
  /** Correo precargado en el buscador al abrir (p. ej. el de la ficha del representante). */
  correoInicial?: string | null;
  /** Nota contextual bajo el campo de correo. */
  hint?: React.ReactNode;
}) {
  const [estado, setEstado] = useState<Estado>("buscar");
  const [correo, setCorreo] = useState("");

  useEffect(() => {
    if (open && correoInicial) setCorreo(correoInicial);
  }, [open, correoInicial]);
  const [encontrado, setEncontrado] = useState<UsuarioResumen | null>(null);
  const [creado, setCreado] = useState<UsuarioCreadoResponse | null>(null);
  const [crearForm, setCrearForm] = useState({
    nombre: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    rol: "REPRESENTANTE_LEGAL",
  });
  const [notificarAsociacion, setNotificarAsociacion] = useState(true);
  const [notificarBienvenida, setNotificarBienvenida] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEstado("buscar");
    setCorreo("");
    setEncontrado(null);
    setCreado(null);
    setCrearForm({ nombre: "", apellidoPaterno: "", apellidoMaterno: "", rol: "REPRESENTANTE_LEGAL" });
    setNotificarAsociacion(true);
    setNotificarBienvenida(true);
    setError(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const buscar = async () => {
    if (!correo.trim()) {
      setError("Ingresa un correo.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.get<UsuarioResumen>(
        `/api/iam/usuarios/buscar?correo=${encodeURIComponent(correo.trim())}`
      );
      setEncontrado(data);
      setEstado("encontrado");
    } catch (e) {
      const ax = e as AxiosError<{ detail?: string }>;
      if (ax.response?.status === 404) {
        setEstado("crear");
      } else {
        setError(ax.response?.data?.detail || ax.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const asociarExistente = async () => {
    if (!encontrado) return;
    setLoading(true);
    setError(null);
    try {
      await onAsociar(encontrado.id, notificarAsociacion);
      reset();
      onClose();
    } catch (e) {
      const ax = e as AxiosError<{ detail?: string; title?: string }>;
      setError(ax.response?.data?.detail || ax.response?.data?.title || ax.message);
    } finally {
      setLoading(false);
    }
  };

  const crearYAsociar = async () => {
    if (!crearForm.nombre.trim() || !crearForm.apellidoPaterno.trim()) {
      setError("Nombre y apellido paterno son obligatorios.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post<UsuarioCreadoResponse>("/api/iam/usuarios", {
        correo: correo.trim().toLowerCase(),
        nombre: crearForm.nombre.trim(),
        apellidoPaterno: crearForm.apellidoPaterno.trim(),
        apellidoMaterno: crearForm.apellidoMaterno.trim() || null,
        rol: crearForm.rol,
        notificar: notificarBienvenida,
      });
      setCreado(data);
      // Asocia el nuevo al recurso del caller
      await onAsociar(data.id, notificarAsociacion);
      setEstado("creado-exito");
    } catch (e) {
      const ax = e as AxiosError<{ detail?: string; title?: string }>;
      setError(ax.response?.data?.detail || ax.response?.data?.title || ax.message);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-surface shadow-overlay">
        <div className="flex items-start justify-between border-b px-5 py-3">
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          <button
            onClick={handleClose}
            className="cursor-pointer rounded p-1 text-fg-faint transition hover:bg-surface-2 hover:text-fg-muted"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          {estado !== "creado-exito" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">
                Correo del usuario
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  type="email"
                  value={correo}
                  onChange={(e) => {
                    setCorreo(e.target.value);
                    setEstado("buscar");
                    setEncontrado(null);
                  }}
                  placeholder="ejemplo@empresa.com"
                  className="flex-1 rounded border px-2 py-1.5 text-sm"
                  disabled={loading || estado === "crear"}
                />
                {estado === "buscar" && (
                  <button
                    type="button"
                    onClick={buscar}
                    disabled={loading || !correo.trim()}
                    className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
                  >
                    <Search className="h-4 w-4" /> Buscar
                  </button>
                )}
              </div>
              {hint && <p className="mt-1 text-xs text-fg-subtle">{hint}</p>}
            </div>
          )}

          {estado === "encontrado" && encontrado && (
            <div className="space-y-2 rounded-lg border border-estado-ok/25 bg-estado-ok-bg p-3 text-sm">
              <div className="flex items-center gap-2 text-estado-ok">
                <Check className="h-4 w-4" />
                <span className="font-semibold">Usuario ya existente. ¿Deseas asociarlo a este Registro Legal?</span>
              </div>
              <dl className="grid gap-1 text-xs text-fg-muted">
                <Row label="Nombre" value={`${encontrado.nombre} ${encontrado.apellidoPaterno} ${encontrado.apellidoMaterno ?? ""}`} />
                <Row label="Correo" value={encontrado.correo} />
                <Row label="Tipo" value={encontrado.tipo} />
                <Row label="Estatus" value={encontrado.estatus} />
              </dl>
              <label className="mt-2 flex items-center gap-2 border-t border-estado-ok/25 pt-2 text-xs text-fg-muted">
                <input
                  type="checkbox"
                  checked={notificarAsociacion}
                  onChange={(e) => setNotificarAsociacion(e.target.checked)}
                />
                Enviar correo de asociación al usuario
              </label>
            </div>
          )}

          {estado === "crear" && (
            <div className="space-y-3 rounded-lg border border-estado-info/25 bg-estado-info-bg p-3">
              <p className="text-sm text-estado-info">
                El correo <strong>{correo}</strong> no existe en el sistema. Captura los datos del nuevo usuario:
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <Field label="Nombre *">
                  <input
                    type="text"
                    required
                    value={crearForm.nombre}
                    onChange={(e) => setCrearForm({ ...crearForm, nombre: e.target.value })}
                    className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
                  />
                </Field>
                <Field label="Apellido paterno *">
                  <input
                    type="text"
                    required
                    value={crearForm.apellidoPaterno}
                    onChange={(e) => setCrearForm({ ...crearForm, apellidoPaterno: e.target.value })}
                    className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
                  />
                </Field>
                <Field label="Apellido materno">
                  <input
                    type="text"
                    value={crearForm.apellidoMaterno}
                    onChange={(e) => setCrearForm({ ...crearForm, apellidoMaterno: e.target.value })}
                    className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
                  />
                </Field>
                <Field label="Rol Keycloak *">
                  <select
                    value={crearForm.rol}
                    onChange={(e) => setCrearForm({ ...crearForm, rol: e.target.value })}
                    className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
                  >
                    {ROLES_OPCIONES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <p className="text-xs text-estado-info">
                El sistema generará una contraseña inicial. El usuario debe cambiarla en su primer ingreso.
              </p>
              <div className="space-y-1 border-t border-estado-info/25 pt-2">
                <label className="flex items-center gap-2 text-xs text-fg-muted">
                  <input
                    type="checkbox"
                    checked={notificarBienvenida}
                    onChange={(e) => setNotificarBienvenida(e.target.checked)}
                  />
                  Enviar correo de bienvenida con la contraseña inicial
                </label>
                <label className="flex items-center gap-2 text-xs text-fg-muted">
                  <input
                    type="checkbox"
                    checked={notificarAsociacion}
                    onChange={(e) => setNotificarAsociacion(e.target.checked)}
                  />
                  Enviar correo de asociación al Registro Legal
                </label>
                {!notificarBienvenida && (
                  <p className="text-xs text-estado-warn">
                    Sin correo de bienvenida, deberás entregar la contraseña al usuario por otro medio
                    (se muestra una sola vez al guardar).
                  </p>
                )}
              </div>
            </div>
          )}

          {estado === "creado-exito" && creado && (
            <div className="space-y-3 rounded-lg border border-estado-ok/25 bg-estado-ok-bg p-4">
              <div className="flex items-center gap-2 text-estado-ok">
                <Check className="h-5 w-5" />
                <span className="text-base font-semibold">Usuario creado y asociado</span>
              </div>
              <dl className="grid gap-1 text-sm text-fg">
                <Row label="Correo" value={creado.correo} />
                <Row label="Rol" value={creado.rol} />
              </dl>
              <div className="rounded border border-estado-warn/25 bg-surface p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-estado-warn">
                  Contraseña inicial (se muestra una sola vez)
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 break-all rounded bg-surface-2 px-2 py-1 font-mono text-sm">{creado.passwordInicial}</code>
                  <button
                    onClick={() => navigator.clipboard.writeText(creado.passwordInicial)}
                    className="cursor-pointer rounded border px-2 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
                    title="Copiar"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
                <p className="mt-2 text-xs text-fg-muted">
                  Comparte esta contraseña con el usuario por canal seguro. Keycloak le exigirá cambiarla en el
                  primer ingreso.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
              <AlertCircle className="mt-0.5 h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t bg-surface-2 px-5 py-3">
          {estado === "buscar" && (
            <button
              onClick={handleClose}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
              disabled={loading}
            >
              Cancelar
            </button>
          )}
          {estado === "encontrado" && (
            <>
              <button
                onClick={() => setEstado("buscar")}
                className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
                disabled={loading}
              >
                Atrás
              </button>
              <button
                onClick={asociarExistente}
                className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
                disabled={loading}
              >
                {loading ? "Asociando…" : "Sí, asociar"}
              </button>
            </>
          )}
          {estado === "crear" && (
            <>
              <button
                onClick={() => setEstado("buscar")}
                className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
                disabled={loading}
              >
                Atrás
              </button>
              <button
                onClick={crearYAsociar}
                className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
                disabled={loading}
              >
                <UserPlus className="h-4 w-4" />
                {loading ? "Creando…" : "Crear y asociar"}
              </button>
            </>
          )}
          {estado === "creado-exito" && (
            <button
              onClick={handleClose}
              className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover"
            >
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="font-semibold">{label}</dt>
      <dd className="break-all text-right">{value}</dd>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-fg-muted">{label}</div>
      {children}
    </div>
  );
}
